import type { EngagementScene } from '../../types';
import { formatUsd } from '../EngagementHub/format';

export type RateNudgeVariant = 'standard' | 'long_term';

/**
 * §9.11 rate-nudge trigger. Fires on ALL creation paths (broadened 2026-06-12;
 * the post_consultation-only gate was removed) against the LIVE hours value in
 * the drawer (not a stored field) — the banner appears/disappears as the
 * expert types:
 *
 *   - hours > billing_account.long_term_threshold_hours → 'long_term'
 *   - hours > billing_account.reduced_threshold_hours   → 'standard'
 *   - otherwise                                         → null
 *
 * The threshold comparison is internal trigger plumbing only — per §6.7/§6.3
 * the rendered copy NEVER mentions thresholds, brackets, customer rates, or
 * customer cost.
 *
 * Amendment re-fire behavior is specified in §9.11 but the amendment-
 * composition surface doesn't exist in this prototype — out of scope here.
 */
export function rateNudgeVariant(
  scene: EngagementScene,
  hours: number | null
): RateNudgeVariant | null {
  const ba = scene.billing_account;
  return rateNudgeVariantFor(hours, ba.reduced_threshold_hours, ba.long_term_threshold_hours);
}

/**
 * Threshold-keyed twin for live data (ENG-5605). On live surfaces the thresholds
 * come from `expertOfferContext` — an expert-side field carrying hours only
 * (ENG-5604 G7) — rather than the customer's billing account, which the api
 * denies to the expert. One trigger implementation, two sources.
 */
export function rateNudgeVariantFor(
  hours: number | null,
  reducedThresholdHours: number,
  longTermThresholdHours: number
): RateNudgeVariant | null {
  if (hours === null) return null;
  if (hours > longTermThresholdHours) return 'long_term';
  if (hours > reducedThresholdHours) return 'standard';
  return null;
}

interface RateNudgeProps {
  variant: RateNudgeVariant;
  /** Live [N] — the currently-entered NTE hours value. */
  hours: number;
  /** Live $[expert_rate] — the currently-entered rate; null while blank. */
  expert_rate_amount_cents: number | null;
  /** §11.2 #18 option (a) — focuses the existing Your-rate input. */
  onAdjustRate: () => void;
  onDismiss: () => void;
}

/**
 * §9.11 rate-nudge callout — renders above the Your rate section.
 *
 * Copy is LOCKED (2026-06-12 design pass) — the two body strings below must
 * render verbatim, with [N] = live hours and $[expert_rate] = the live rate.
 * When no rate has been entered yet the trailing "Your rate: …" sentence is
 * omitted (the spec interpolates a rate value; there is none to interpolate
 * — TODO: confirm with eng).
 *
 * Style per §9.11: muted attention-tinted callout, deliberately LIGHTER than
 * the customer-side over-threshold banner (`HireExpertDrawer/
 * PricingPreviewSection.tsx GovernanceBanner`, yellow-900/20) — this is an
 * informational nudge, not a warning. Dismissable for this engagement only;
 * dismissal is drawer-instance state held by the composition root, so once
 * dismissed it never re-renders in this drawer regardless of further hours
 * edits.
 */
export default function RateNudge({
  variant,
  hours,
  expert_rate_amount_cents,
  onAdjustRate,
  onDismiss,
}: RateNudgeProps): JSX.Element {
  const body =
    variant === 'long_term'
      ? `This is a ${hours}-hour engagement — sustained, long-term work. Most experts price long-term engagements below their consultation rate, and competitive rates win more sustained work.`
      : `This is a ${hours}-hour engagement — well beyond a single consultation. Many experts set a lower rate for sustained work; a sharper rate can make your offer easier to approve.`;

  const rateSentence =
    expert_rate_amount_cents !== null
      ? ` Your rate: ${formatNudgeRate(expert_rate_amount_cents)}/hr.`
      : '';

  return (
    <div
      role="note"
      className="flex flex-col gap-8 rounded-md border border-yellow-700/25 bg-yellow-50/60 px-16 py-12 dark:bg-yellow-900/10"
    >
      <div className="flex items-start justify-between gap-12">
        <p className="text-on-surface body-2">{`${body}${rateSentence}`}</p>
        {/* WCAG 2.5.5: this is a real tap target, not 10px of text. AC2 covers
            this drawer at 375px, where the old `util-caption` control was
            effectively unhittable. The × is decorative — the button's name
            comes from the label beside it. (ENG-5448 QA.) */}
        <button
          type="button"
          onClick={onDismiss}
          className="-me-8 -mt-8 flex min-h-48 shrink-0 items-center gap-4 px-8 text-on-surface-muted body-2 hover:text-on-surface"
        >
          <span aria-hidden>×</span>
          Dismiss
        </button>
      </div>
      <button
        type="button"
        onClick={onAdjustRate}
        className="flex min-h-48 items-center self-start font-medium text-on-surface underline body-2 hover:text-on-surface-muted"
      >
        → Set your rate for this engagement
      </button>
    </div>
  );
}

/**
 * "$250" for whole-dollar rates, "$262.50" otherwise — the locked copy reads
 * "$[expert_rate]/hr" and a trailing ".00" on a round rate would be noise.
 */
function formatNudgeRate(cents: number): string {
  return formatUsd(cents, { showCents: cents % 100 !== 0 });
}
